import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notesApi, taskNotesApi } from '../lib/api.ts'

/**
 * Notes attached to tasks, from both ends (ADR 0012).
 *
 * A link shows up in two lists at once — the task's notes and the note's tasks — so every mutation
 * invalidates both roots rather than the one the click came from. Refetching a list of at most a
 * handful of rows is cheaper than reasoning about which of the two views is currently mounted.
 */
export const taskNoteKeys = {
  ofTask: (taskId: number) => ['task-notes', taskId] as const,
  ofNote: (noteId: number) => ['note-tasks', noteId] as const,
}

export function useNotes() {
  return useQuery({
    queryKey: ['notes'],
    queryFn: async () => (await notesApi.list()).notes,
  })
}

export function useTaskNotes(taskId: number | null) {
  return useQuery({
    queryKey: taskNoteKeys.ofTask(taskId ?? 0),
    queryFn: async () => {
      if (taskId === null) throw new Error('Task notes queried with no task id.')
      return (await taskNotesApi.list(taskId)).notes
    },
    enabled: taskId !== null,
  })
}

export function useNoteTasks(noteId: number | null) {
  return useQuery({
    queryKey: taskNoteKeys.ofNote(noteId ?? 0),
    queryFn: async () => {
      if (noteId === null) throw new Error('Note tasks queried with no note id.')
      return (await taskNotesApi.forNote(noteId)).tasks
    },
    enabled: noteId !== null,
  })
}

function useInvalidateLinks(): () => void {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['task-notes'] })
    void queryClient.invalidateQueries({ queryKey: ['note-tasks'] })
  }
}

export function useLinkNote() {
  const invalidate = useInvalidateLinks()
  return useMutation({
    mutationFn: ({ taskId, noteId }: { taskId: number; noteId: number }) =>
      taskNotesApi.link(taskId, noteId),
    onSuccess: invalidate,
  })
}

export function useUnlinkNote() {
  const invalidate = useInvalidateLinks()
  return useMutation({
    mutationFn: ({ taskId, noteId }: { taskId: number; noteId: number }) =>
      taskNotesApi.unlink(taskId, noteId),
    onSuccess: invalidate,
  })
}
